import { ApiCollectionResponse } from 'types/api_response';
import { RiskPoolOrganization } from '../../_types/risk_pool';
import { randCity, randNumber } from '@ngneat/falso';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'risk_pool_mock_data';
const ORGANIZATIONS_STORAGE_KEY = 'risk_pool_organizations';

// Generate a random number between min and max (inclusive)
const randomInt = (min: number, max: number) => randNumber({ min, max });

// Generate a random trend value
const randomTrend = () => {
  const trends: RiskPoolOrganization['trend'][] = ['UP', 'RISING', 'NEUTRAL', 'DECLINING', 'DOWN'];
  return trends[Math.floor(Math.random() * trends.length)];
};

// Generate a random department type
const randomDepartmentType = () => {
  const types = ['PD', 'FD', 'Jail'];
  return types[Math.floor(Math.random() * types.length)];
};

// Generate compliance performance based on compliance percentage
const generateCompliancePerformance = (compliance: number): RiskPoolOrganization['compliancePerformance'] => {
  if (compliance >= 90) return 'meetsAll';
  if (compliance >= 51) return 'meetsMajority';
  if (compliance < 10) return 'meetsNone';
  return 'meetsSome';
};

// Generate a single mock organization, with optional modifiedPolicyCount
const generateMockOrganization = (id: string, name: string, modifiedPolicyCountOverride?: number): RiskPoolOrganization => {
  const totalPolicyCount = randomInt(30, 50);
  const modifiedPolicyCount =
    typeof modifiedPolicyCountOverride === 'number'
      ? modifiedPolicyCountOverride
      : randomInt(0, 15);
  const publishedPolicyCount = Math.floor(totalPolicyCount * randomInt(90, 100) / 100);
  const compliance = randomInt(0, 100);

  return {
    id,
    name,
    trend: randomTrend(),
    compliance,
    compliancePerformance: generateCompliancePerformance(compliance),
    totalPolicyCount,
    modifiedPolicyCount,
    publishedPolicyCount,
  };
};

// Generate and store the persistent set of organizations
const generateAndStoreOrganizations = (): Array<{ id: string; name: string; }> => {
  const organizations: Array<{ id: string; name: string; }> = [];

  for (let i = 0; i < 10; i++) {
    organizations.push({
      id: uuidv4(),
      name: `${randCity()} ${randomDepartmentType()}`,
    });
  }

  sessionStorage.setItem(ORGANIZATIONS_STORAGE_KEY, JSON.stringify(organizations));
  return organizations;
};

// Get the persistent organizations from storage or generate new if not present
export const getPersistentOrganizations = (): Array<{ id: string; name: string; }> => {
  const storedOrganizations = sessionStorage.getItem(ORGANIZATIONS_STORAGE_KEY);
  if (storedOrganizations) {
    return JSON.parse(storedOrganizations);
  }
  return generateAndStoreOrganizations();
};

// Generate and store all mock data using persistent organizations
const generateAndStoreMockData = (params: Record<string, any> = {}): RiskPoolOrganization[] => {
  const organizations = getPersistentOrganizations();
  const totalCount = organizations.length;
  const zeroModifiedCount = Math.round(totalCount * 0.3);
  const mockData: RiskPoolOrganization[] = [];

  for (let i = 0; i < totalCount; i++) {
    const org = organizations[i];
    if (i < zeroModifiedCount) {
      mockData.push(generateMockOrganization(org.id, org.name, 0));
    } else {
      mockData.push(generateMockOrganization(org.id, org.name));
    }
  }

  const storageKey = getStorageKey(params);
  sessionStorage.setItem(storageKey, JSON.stringify(mockData));
  return mockData;
};

// Generate a storage key that includes date range
const getStorageKey = (params: Record<string, any>) => {
  const { startDate, endDate } = params;
  return `${STORAGE_KEY}_${startDate || 'no_start'}_${endDate || 'no_end'}`;
};

// Get mock data from storage or generate new if not present
const getMockData = (params: Record<string, any> = {}): RiskPoolOrganization[] => {
  const storageKey = getStorageKey(params);
  const storedData = sessionStorage.getItem(storageKey);
  if (storedData) {
    return JSON.parse(storedData);
  }
  return generateAndStoreMockData(params);
};

// Generate mock data for risk pool metrics
export const generateRiskPoolMetrics = (
  orgId: string,
  params: Record<string, any> = {}
): RiskPoolOrganization[] => {
  return getMockData(params);
};
